import { createSlice } from '@reduxjs/toolkit';
import { assetApi } from '../../services/assetApi';

const initialState = {
  assets: [],
  assetRequests: [],
  loading: false,
  error: null,
  filters: {
    status: '',
    category: '',
    branchId: '',
    search: '',
  },
  requestFilters: {
    status: '',
    assetType: '',
    employeeId: '',
    search: '',
  },
  pagination: {
    currentPage: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  requestPagination: {
    currentPage: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  stats: {
    totalAssets: 0,
    availableAssets: 0,
    assignedAssets: 0,
    underMaintenanceAssets: 0,
    retiredAssets: 0,
    totalValue: 0,
  },
  requestStats: {
    totalRequests: 0,
    submittedRequests: 0,
    approvedRequests: 0,
    fulfilledRequests: 0,
  },
  selectedAsset: null,
  selectedRequest: null,
};

const assetSlice = createSlice({
  name: 'asset',
  initialState,
  reducers: {
    // Asset filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        status: '',
        category: '',
        branchId: '',
        search: '',
      };
      state.pagination.currentPage = 1;
    },

    // Asset request filters
    setRequestFilters: (state, action) => {
      state.requestFilters = { ...state.requestFilters, ...action.payload };
      state.requestPagination.currentPage = 1;
    },
    clearRequestFilters: (state) => {
      state.requestFilters = {
        status: '',
        assetType: '',
        employeeId: '',
        search: '',
      };
      state.requestPagination.currentPage = 1;
    },

    // Pagination
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setRequestCurrentPage: (state, action) => {
      state.requestPagination.currentPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setRequestPagination: (state, action) => {
      state.requestPagination = { ...state.requestPagination, ...action.payload };
    },

    // Stats
    setStats: (state, action) => {
      state.stats = action.payload;
    },
    setRequestStats: (state, action) => {
      state.requestStats = action.payload;
    },

    // Selections
    setSelectedAsset: (state, action) => {
      state.selectedAsset = action.payload;
    },
    clearSelectedAsset: (state) => {
      state.selectedAsset = null;
    },
    setSelectedRequest: (state, action) => {
      state.selectedRequest = action.payload;
    },
    clearSelectedRequest: (state) => {
      state.selectedRequest = null;
    },

    // Loading and error
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get assets
      .addMatcher(
        assetApi.endpoints.getAssets.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        assetApi.endpoints.getAssets.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.assets = action.payload.data;
          state.pagination = action.payload.pagination;
          state.stats = action.payload.stats;
        }
      )
      .addMatcher(
        assetApi.endpoints.getAssets.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        }
      )

      // Get single asset
      .addMatcher(
        assetApi.endpoints.getAsset.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        assetApi.endpoints.getAsset.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.selectedAsset = action.payload.data;
        }
      )
      .addMatcher(
        assetApi.endpoints.getAsset.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        }
      )

      // Get asset requests
      .addMatcher(
        assetApi.endpoints.getAssetRequests.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        assetApi.endpoints.getAssetRequests.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.assetRequests = action.payload.data;
          state.requestPagination = action.payload.pagination;
          state.requestStats = action.payload.stats;
        }
      )
      .addMatcher(
        assetApi.endpoints.getAssetRequests.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message;
        }
      );
  },
});

export const {
  setFilters,
  clearFilters,
  setRequestFilters,
  clearRequestFilters,
  setCurrentPage,
  setRequestCurrentPage,
  setPagination,
  setRequestPagination,
  setStats,
  setRequestStats,
  setSelectedAsset,
  clearSelectedAsset,
  setSelectedRequest,
  clearSelectedRequest,
  setLoading,
  setError,
} = assetSlice.actions;

export default assetSlice.reducer;
